import { Package } from '@revenuecat/purchases-js'
import { DuprProduct } from '@/services/checkout'
import { parsePeriod } from '@/pages/external/components/checkout/utils/pricing.utils'
import { getProductDisplayName, UI_PROPERTY_KEYS } from '@/constants/UiPropertyKeys'

/**
 * Generic product interface for consistent display and interaction
 */
export interface GenericProductInfo {
  id: string
  displayName: string
  price: number
  period: string
  currency?: string
  description?: string
  features?: string[]
  trialDays?: number
  priceReduction?: number
  isOneTime?: boolean
}

/**
 * Purchase request interface that can handle both product types
 */
export interface PurchaseRequest {
  productId: string
  productType: 'dupr' | 'revenuecat'
  originalProduct: DuprProduct | Package
}

/**
 * Strategy interface for handling different product types
 */
export interface ProductStrategy {
  /**
   * Convert the product to generic display format
   */
  toGenericProduct(): GenericProductInfo

  /**
   * Create a purchase request for this product
   */
  createPurchaseRequest(): PurchaseRequest

  /**
   * Get tracking data for analytics
   */
  getTrackingData(): Record<string, any>

  /**
   * Initiate purchase flow
   */
  purchase(): Promise<string | void>
}

/**
 * Strategy for handling DuprProduct
 */
export class DuprProductStrategy implements ProductStrategy {
  constructor(
    private product: DuprProduct,
    private onPurchaseInitiated?: (request: any) => Promise<string>
  ) {}

  toGenericProduct(): GenericProductInfo {
    // Use product-level display name override if set in properties
    const displayName = getProductDisplayName(this.product.properties, this.product.displayName)

    return {
      id: this.product.productId,
      displayName,
      price: this.product.price / 100, // Convert cents to dollars
      period: this.product.period, // Keep raw ISO format (P1Y, P12M, etc.) for parsing in display
      currency: 'USD', // Assuming USD for DUPR products
      description: this.product.period === 'P0D'
        ? `${displayName} - one-time purchase`
        : `${displayName} - ${parsePeriod(this.product.period).displayNameLong}`,
      features: (this.product.properties?.[UI_PROPERTY_KEYS.PRODUCT.HIDE_BENEFACTOR] !== 'true'
        && this.product.promotion?.benefactor?.displayName?.trim())
        ? [`Sponsored by ${this.product.promotion.benefactor.displayName}`]
        : [],
      trialDays: this.product.promotion?.duprDiscount?.trialDays,
      priceReduction: this.product.promotion?.duprDiscount?.priceReduction,
      isOneTime: this.product.period === 'P0D',
    }
  }

  createPurchaseRequest(): PurchaseRequest {
    return {
      productId: this.product.productId,
      productType: 'dupr',
      originalProduct: this.product
    }
  }

  getTrackingData(): Record<string, any> {
    return {
      product_id: this.product.productId,
      product_name: this.product.displayName,
      product_type: 'dupr',
      price: this.product.price / 100, // Convert cents to dollars for tracking
      period: this.product.period,
      has_promotion: !!this.product.promotion?.duprDiscount,
      benefactor: this.product.promotion?.benefactor?.displayName
    }
  }

  async purchase(): Promise<string | void> {
    if (!this.onPurchaseInitiated) {
      console.error('Purchase handler not provided')
      throw new Error('Purchase handler not provided')
    }

    const stripeRequest = {
      duprProductId: this.product.productId,
    }
    
    const clientSecret = await this.onPurchaseInitiated(stripeRequest)
    return clientSecret
  }
}

/**
 * Strategy for handling RevenueCat Package
 */
export class RevenueCatProductStrategy implements ProductStrategy {
  constructor(
    private rcPackage: Package,
    private revenueCat: any, // RevenueCat instance
    private onPurchaseInitiated?: () => void
  ) {}

  toGenericProduct(): GenericProductInfo {
    const priceInCents = this.rcPackage.webBillingProduct?.currentPrice?.amount ?? 0
    const price = priceInCents / 100 // Convert cents to dollars
    const currency = this.rcPackage.webBillingProduct?.currentPrice?.currency ?? 'USD'
    const displayName = this.rcPackage.webBillingProduct?.displayName ?? this.rcPackage.identifier

    // Use normalPeriodDuration for accurate period parsing (e.g., P1Y, P12M, P1M)
    const periodDuration = this.rcPackage.webBillingProduct?.normalPeriodDuration ?? 'P1M'
    const periodInfo = parsePeriod(periodDuration)

    // Extract trial days from RevenueCat package
    let trialDays: number | undefined
    
    // Check rcBillingProduct for trial information (the correct field)
    const rcBillingProduct = this.rcPackage.rcBillingProduct || this.rcPackage.webBillingProduct
    
    if (rcBillingProduct) {
      // Check freeTrialPhase first
      if (rcBillingProduct.freeTrialPhase?.periodDuration) {
        const trialPeriod = rcBillingProduct.freeTrialPhase.periodDuration
        // Parse ISO 8601 duration
        const dayMatch = trialPeriod.match(/^P(?:(\d+)D)$/)
        const weekMatch = trialPeriod.match(/^P(?:(\d+)W)$/)
        
        if (dayMatch && dayMatch[1]) {
          trialDays = parseInt(dayMatch[1], 10)
        } else if (weekMatch && weekMatch[1]) {
          trialDays = parseInt(weekMatch[1], 10) * 7 // Convert weeks to days
        }
        
      }

      // Fallback: check defaultSubscriptionOption.trial
      if (!trialDays && rcBillingProduct.defaultSubscriptionOption?.trial) {
        const trial = rcBillingProduct.defaultSubscriptionOption.trial
        
        if (trial.period?.number && trial.period?.unit) {
          if (trial.period.unit === 'day') {
            trialDays = trial.period.number
          } else if (trial.period.unit === 'week') {
            trialDays = trial.period.number * 7
          } else if (trial.period.unit === 'month') {
            trialDays = trial.period.number * 30 // Approximate
          }
          
        }
      }
    }

    return {
      id: this.rcPackage.identifier,
      displayName,
      price,
      period: periodDuration, // Keep raw ISO format (P1Y, P1M, etc.) for parsing in display
      currency,
      description: `${displayName} - ${periodInfo.displayNameLong}`,
      features: ['Standard DUPR+ subscription'],
      trialDays,
      priceReduction: 0,
      isOneTime: periodDuration === 'P0D',
    }
  }

  createPurchaseRequest(): PurchaseRequest {
    return {
      productId: this.rcPackage.identifier,
      productType: 'revenuecat',
      originalProduct: this.rcPackage
    }
  }

  getTrackingData(): Record<string, any> {
    const priceInCents = this.rcPackage.webBillingProduct?.currentPrice?.amount ?? 0
    const priceInDollars = priceInCents / 100 // Convert cents to dollars for tracking
    const periodDuration = this.rcPackage.webBillingProduct?.normalPeriodDuration ?? 'P1M'
    const periodInfo = parsePeriod(periodDuration)

    return {
      product_id: this.rcPackage.identifier,
      product_name: this.rcPackage.webBillingProduct?.displayName ?? this.rcPackage.identifier,
      product_type: 'revenuecat',
      price: priceInDollars,
      period: periodInfo.displayNameLong,
      package_type: this.rcPackage.packageType,
      period_duration: periodDuration
    }
  }

  async purchase(): Promise<void> {
    if (!this.revenueCat) {
      throw new Error('RevenueCat instance not provided')
    }

    try {
      await this.revenueCat.purchasePackage(this.rcPackage)

      if (this.onPurchaseInitiated) {
        this.onPurchaseInitiated()
      }
    } catch (error) {
      console.error('RevenueCat purchase failed:', error)
      throw error
    }
  }
}

/**
 * Factory for creating product strategies
 */
export class ProductStrategyFactory {
  static createDuprStrategy(
    product: DuprProduct,
    onPurchaseInitiated?: (request: any) => Promise<string>
  ): DuprProductStrategy {
    return new DuprProductStrategy(product, onPurchaseInitiated)
  }

  static createRevenueCatStrategy(
    rcPackage: Package,
    revenueCat: any,
    onPurchaseInitiated?: () => void
  ): RevenueCatProductStrategy {
    return new RevenueCatProductStrategy(rcPackage, revenueCat, onPurchaseInitiated)
  }
}

/**
 * Utility to determine if a product is a DuprProduct
 */
export function isDuprProduct(product: DuprProduct | Package): product is DuprProduct {
  return 'productId' in product && 'promotion' in product
}

/**
 * Utility to determine if a product is a RevenueCat Package
 */
export function isRevenueCatPackage(product: DuprProduct | Package): product is Package {
  return 'identifier' in product && 'packageType' in product
}